// Hooks
import { useBasicPaginatedResource } from "@Common/hooks/Pagination/useBasicPaginatedResource";

// API
import { getAccessSettingsUsers } from "@Common/api/users/queries";

// Utils
import { and } from "@Common/helpers/sonar";

// Types
import { IPageParam } from "@/Common/api/types/pagination";
import { ILocation } from "@Common/interfaces/location";
import { IUser } from "@Common/interfaces/user";

const PAGE_SIZE = 200;

export const useAccessSettingsUsersPaginatedQuery = ({ location }: { location: ILocation }) => {
	const locationId = location?.locationId;

	const { data, ...otherResults } = useBasicPaginatedResource<IUser>({
		queryKey: ["AccessSettingUsers", locationId],
		queryFn: async ({ pageParam }) => {
			const typedPageParam = pageParam as IPageParam<string>;
			if (!typedPageParam || !("after" in typedPageParam) || !("first" in typedPageParam)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}

			const users = await getAccessSettingsUsers(locationId, {
				first: typedPageParam.first,
				after: typedPageParam.after,
			});

			if (!users) {
				return {
					items: [],
					pageInfo: {
						hasNextPage: false,
						endCursor: "",
					},
				};
			}

			return users;
		},

		getNextPageParam: (lastPage) => {
			const { pageInfo } = lastPage ?? {};
			return pageInfo?.hasNextPage ? { first: PAGE_SIZE, after: pageInfo.endCursor ?? undefined } : undefined;
		},
		initialPageParam: {
			first: PAGE_SIZE,
			after: null,
		},
		fetchAllPages: true,
		enabled: and(Boolean(locationId)),
		staleTime: Infinity,
	});

	return {
		data,
		...otherResults,
	};
};
